import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { crmContactService } from '@/services/crmContactService';
import { gmailService } from '@/services/gmailService';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';
import UGCLayout from '@/components/layouts/UGCLayout';

const UGCCRMPage = () => {
  const { profile } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts', 'emails', 'settings'
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    notes: '',
    tags: ''
  });

  const ugcNavItems = [
    { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
    checkGmailConnection();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await crmContactService.listContacts();
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkGmailConnection = async () => {
    try {
      const status = await gmailService.getConnectionStatus();
      setGmailConnected(status.connected);
    } catch (err) {
      console.error('Failed to check Gmail status:', err);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    try {
      const newContact = await crmContactService.createContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        title: formData.title,
        notes: formData.notes,
        tags: formData.tags
          ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
          : []
      });

      setContacts([newContact, ...contacts]);
      setFormData({
        name: '',
        email: '',
        phone: '',
        title: '',
        notes: '',
        tags: ''
      });
      setShowAddContactModal(false);
    } catch (err) {
      console.error('Failed to add contact:', err);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;

    try {
      await crmContactService.deleteContact(contactId);
      setContacts(contacts.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleConnectGmail = () => {
    const authUrl = gmailService.getAuthUrl();
    window.location.href = authUrl;
  };

  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    const term = searchTerm.toLowerCase();
    return contacts.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term) ||
      (c.title && c.title.toLowerCase().includes(term))
    );
  }, [contacts, searchTerm]);

  const renderContactsList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Contacts</h2>
          <p className="text-sm text-amber-100/80 mt-1">{contacts.length} total contacts</p>
        </div>
        <Button onClick={() => setShowAddContactModal(true)} className="bg-amber-500 hover:bg-amber-600 text-white">
          <Icon name="Plus" size={16} className="mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Icon name="Search" size={18} className="absolute left-3 top-3.5 text-amber-100/70 pointer-events-none" />
        <input
          type="text"
          placeholder="Search by name, email, or title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-white/10 rounded-lg bg-white/5 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </div>

      {/* Contacts Table */}
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Email</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Title</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Tags</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {filteredContacts.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-amber-100/80">
                  {contacts.length === 0 ? 'No contacts yet. Add one to get started!' : 'No contacts match your search.'}
                </td>
              </tr>
            ) : (
              filteredContacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-white">{contact.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <a href={`mailto:${contact.email}`} className="text-amber-300 hover:underline text-sm">
                      {contact.email}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-amber-100/80">{contact.title || '—'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {contact.tags?.map((tag) => (
                        <span key={tag} className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                        onClick={() => alert('Email feature coming soon')}
                      >
                        <Icon name="Mail" size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-amber-100 hover:bg-white/10"
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <Icon name="Trash" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEmailsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Email Integration</h2>
          <p className="text-sm text-amber-100/80 mt-1">Connect Gmail to send and receive emails from the app</p>
        </div>
        {!gmailConnected && (
          <Button onClick={handleConnectGmail} className="bg-blue-500 hover:bg-blue-600 text-white">
            <Icon name="Mail" size={16} className="mr-2" />
            Connect Gmail
          </Button>
        )}
      </div>

      {gmailConnected ? (
        <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-6">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" size={24} className="text-green-300" />
            <div>
              <h3 className="font-semibold text-white">Gmail Connected</h3>
              <p className="text-sm text-amber-100/80">You can now send emails directly from the app</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-6">
          <div className="flex items-center gap-3">
            <Icon name="AlertCircle" size={24} className="text-yellow-300" />
            <div>
              <h3 className="font-semibold text-white">Gmail Not Connected</h3>
              <p className="text-sm text-amber-100/80">Click the button above to authorize Gmail access</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold text-white mb-4">Features</h3>
        <ul className="space-y-2">
          <li className="flex items-start gap-3">
            <Icon name="Check" size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-white">Send emails directly from PitchLab</span>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-white">Auto-log sent emails to your CRM</span>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-white">Track email history with contacts</span>
          </li>
          <li className="flex items-start gap-3">
            <Icon name="Check" size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-white">Generate AI-powered outreach emails</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">CRM Settings</h2>
        <p className="text-sm text-amber-100/80 mt-1">Manage your CRM preferences</p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold text-white mb-4">Email Templates</h3>
        <p className="text-sm text-amber-100/80 mb-4">Create templates for common outreach scenarios</p>
        <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
          <Icon name="Plus" size={16} className="mr-2" />
          Create Template
        </Button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold text-white mb-4">Privacy & Security</h3>
        <p className="text-sm text-amber-100/80 mb-4">Your data is encrypted and never shared with third parties.</p>
        {gmailConnected && (
          <Button variant="destructive" onClick={() => gmailService.disconnect()}>
            <Icon name="LogOut" size={16} className="mr-2" />
            Disconnect Gmail
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <UGCLayout className="pb-20">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />

      <main className="container max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8 rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-amber-500/10 p-8">
          <h1 className="text-4xl font-bold text-white mb-2">UGC Creator CRM</h1>
          <p className="text-amber-100/80">Manage brand relationships, track deals, and send emails without leaving the app</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {[
            { id: 'contacts', label: 'Contacts', icon: 'Users' },
            { id: 'emails', label: 'Emails & Gmail', icon: 'Mail' },
            { id: 'settings', label: 'Settings', icon: 'Settings' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium flex items-center gap-2 transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-amber-400 text-white'
                  : 'border-transparent text-amber-200/70 hover:text-white'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="text-center py-12">
            <Icon name="Loader" size={32} className="animate-spin mx-auto text-amber-300 mb-4" />
            <p className="text-amber-100/80">Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'contacts' && renderContactsList()}
            {activeTab === 'emails' && renderEmailsTab()}
            {activeTab === 'settings' && renderSettingsTab()}
          </>
        )}

        {/* Add Contact Modal */}
        {showAddContactModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-card border border-border rounded-lg p-8 w-full max-w-md">
              <h2 className="text-2xl font-bold text-foreground mb-6">Add New Contact</h2>
              <form onSubmit={handleAddContact} className="space-y-4">
                <input
                  type="text"
                  placeholder="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                  type="text"
                  placeholder="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <textarea
                  placeholder="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                />
                <input
                  type="text"
                  placeholder="Tags (comma-separated)"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddContactModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">
                    Add Contact
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCCRMPageWithProvider = () => (
  <NovaChatProvider>
    <UGCCRMPage />
  </NovaChatProvider>
);

export default UGCCRMPageWithProvider;
